import React, { useEffect, useState } from "react";
import { Link as NavLink } from "gatsby";
import { useLocation } from "@reach/router";
import { Sun, Moon, Menu, X, Edit } from "lucide-react";
import clsx from "clsx";

interface NavbarProps {
  theme: "light" | "dark";
  toggleTheme: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ theme, toggleTheme }) => {
  const location = useLocation();

  // Decide which pages should start "big" (isShrunk = false)
  const pagesToStartBig = ["/", "/contact", "/publications", "/about", "/press"];
  const shouldStartBig = pagesToStartBig.includes(location.pathname);

  // Set initial state based on path
  const [isShrunk, setIsShrunk] = useState(() => !shouldStartBig);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    // Update login state when location changes
    const token = localStorage.getItem("githubToken");
    setIsLoggedIn(!!token);
  }, [location]);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;

    // Expand navbar if scrolling up to the top, shrink otherwise
    if (currentScrollY === 0) {
      setIsShrunk(false);
    } else if (currentScrollY > lastScrollY) {
      setIsShrunk(true);
    }

    setLastScrollY(currentScrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const navLinks = [
    { name: "Portfolio", path: "/" },
    { name: "About", path: "/about" },
    { name: "Press", path: "/press" },
    { name: "Publications", path: "/publications" },
    { name: "Contact", path: "/contact" },
  ];

  const activeClassName = "text-blue-600 font-semibold dark:text-blue-400";
  const inactiveClassName =
    "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400";

  return (
    <nav
      className={clsx(
        "fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-white dark:bg-gray-800 shadow",
        { "py-2 px-4": isShrunk, "py-4 px-6": !isShrunk }
      )}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <NavLink
          to="/"
          className={clsx(
            "font-montserrat font-bold text-blue-600 dark:text-blue-400 transition-colors",
            {
              "text-lg": isShrunk,
              "text-xl": !isShrunk,
            }
          )}
        >
          Ryan Hardesty Lewis
        </NavLink>

        <div className="hidden md:flex space-x-4 items-center">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              getProps={({ isCurrent }) => ({
                className: clsx(
                  "px-2 py-1 transition-colors",
                  isCurrent ? activeClassName : inactiveClassName
                ),
              })}
            >
              {link.name.toUpperCase()}
            </NavLink>
          ))}

          <button onClick={toggleTheme} className="focus:outline-none ml-4">
            {theme === "dark" ? (
              <Sun className="w-6 h-6 text-yellow-400" />
            ) : (
              <Moon className="w-6 h-6 text-gray-600" />
            )}
          </button>

          {isLoggedIn && (
            <NavLink
              to="/edit"
              state={{ location }}
              className="ml-4"
              aria-label="Edit content"
            >
              <Edit className="w-6 h-6 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400" />
            </NavLink>
          )}
        </div>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden focus:outline-none"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden mt-4 px-4">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              getProps={({ isCurrent }) => ({
                className: clsx(
                  "block py-2",
                  isCurrent ? activeClassName : inactiveClassName
                ),
              })}
            >
              {link.name.toUpperCase()}
            </NavLink>
          ))}
          <button onClick={toggleTheme} className="block mt-4">
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          {isLoggedIn && (
            <NavLink
              to="/edit"
              onClick={() => setMenuOpen(false)}
              className="block mt-4"
            >
              Edit
            </NavLink>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
